import { decisionDossierMail } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { api, corps, ErreurApi, type FicheTache, lire, requeteMoi, requetes } from '../api'
import { cheminLisible, LIBELLES_TYPE_PIECE } from '../libelles'
import { DebitsDeLaPiece } from './Acheminement'
import { ChoixCherchable } from './ChoixCherchable'
import { ChampIndication } from './Dictee'
import { CONSEQUENCES_REGLEMENT } from './reglement-trouve'
import { entitesPourPiece } from './RattacherPiece'
import { suivreRangement } from './SuiviRangement'

// Le dossier d'un mail : le mail et toutes ses pièces, tranchés en une seule décision.
// L'entité, l'indication (écrite ou dictée), le classement et le règlement valent pour
// l'ensemble ; chaque pièce se corrige à part (une autre entité, un reçu à écarter). Ce que
// la lecture propose (paiement attendu, tâche « Payer ») se crée à la validation.

type Dossier = NonNullable<FicheTache['dossier']>
type PieceDossier = Dossier['pieces'][number]
type Correction = { ecarter: boolean; entiteId: string; suites: boolean }

export function DossierMail({
  tacheId,
  dossier,
  apres,
}: {
  tacheId: string
  dossier: Dossier
  apres: (r: FicheTache) => void
}) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const entites = useQuery(requetes.entites).data ?? []
  const idIndication = useId()
  const idEntite = useId()
  const idDossier = useId()
  const [entiteId, setEntiteId] = useState(
    dossier.entiteProposee ?? dossier.pieces.find((p) => p.aClasser)?.entiteId ?? '',
  )
  const [indication, setIndication] = useState('')
  const [retenir, setRetenir] = useState(false)
  const [dossierId, setDossierId] = useState('')
  const [reglement, setReglement] = useState<'' | 'societe' | 'avance'>('')
  const [corrections, setCorrections] = useState<Record<string, Correction>>({})
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const plan = useQuery({ ...requetes.plan(entiteId), enabled: entiteId !== '' })
  const dossiersPlan = (plan.data?.dossiers ?? []).filter((d) => d.enService && d.recoitFichiers)

  const aRattacher = dossier.pieces.filter((p) => p.aRattacher)
  // Même règle que le serveur, pièce par pièce (cloison, boîte personnelle) ; pour le choix
  // commun, les entités où toutes les pièces à rattacher peuvent aller.
  const pourPiece = (p: PieceDossier) => entitesPourPiece(p, entites, moi)
  const communes = aRattacher.reduce(
    (liste, p) => {
      const permises = new Set(pourPiece(p).map((e) => e.id))
      return liste.filter((e) => permises.has(e.id))
    },
    entites.filter((e) => !e.archivee && moi?.ecriture.includes(e.id)),
  )
  const aRegler = dossier.pieces.some((p) => p.aRegler)
  const aClasser = dossier.pieces.some((p) => p.aClasser || p.aRattacher)
  const correction = (p: PieceDossier): Correction =>
    corrections[p.id] ?? { ecarter: false, entiteId: '', suites: true }
  const corriger = (p: PieceDossier, c: Partial<Correction>) =>
    setCorrections((x) => ({ ...x, [p.id]: { ...correction(p), ...c } }))

  async function valider() {
    setErreur(null)
    setOccupe(true)
    try {
      const texte = indication.trim().slice(0, 500)
      const r = await lire(
        api.taches[':id']['trancher-mail'].$post({
          param: { id: tacheId },
          json: corps(decisionDossierMail, {
            ...(entiteId && { entiteId }),
            ...(texte && { indication: texte }),
            ...(retenir && { retenirFournisseur: true }),
            ...(dossierId && { dossierId }),
            ...(reglement && { reglement }),
            pieces: Object.entries(corrections).map(([pieceId, c]) => ({
              pieceId,
              ecarter: c.ecarter,
              ...(c.entiteId && { entiteId: c.entiteId }),
              suites: c.suites,
            })),
          }),
        }),
      )
      for (const p of aRattacher) if (!correction(p).ecarter) suivreRangement(p.id)
      await client.invalidateQueries()
      apres(r)
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Décision impossible.')
    } finally {
      setOccupe(false)
    }
  }

  return (
    <div className="pile">
      <p className="discret">
        Une seule décision pour le mail et ses pièces ; chaque pièce se corrige à part ci-dessous.
        Rien n’est payé, déclaré ni signé : ce qui est proposé se crée, l’action reste humaine.
      </p>

      {aRattacher.length > 0 && (
        <>
          <div className="champ">
            <label htmlFor={idEntite}>Entité</label>
            <select id={idEntite} value={entiteId} onChange={(e) => setEntiteId(e.target.value)}>
              <option value="">Choisir…</option>
              {communes.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
          </div>
          <ChampIndication id={idIndication} valeur={indication} onChange={setIndication} />
          <div className="champ champ-case">
            <input
              id={`${idEntite}-retenir`}
              type="checkbox"
              checked={retenir}
              onChange={(e) => setRetenir(e.target.checked)}
            />
            <label htmlFor={`${idEntite}-retenir`}>
              Toujours cette entité pour ce fournisseur (ses prochains mails se traitent seuls)
            </label>
          </div>
        </>
      )}

      {aClasser && entiteId && dossiersPlan.length > 0 && (
        <div className="champ">
          <label htmlFor={idDossier}>Dossier de classement</label>
          <ChoixCherchable
            id={idDossier}
            valeur={dossierId}
            onChange={setDossierId}
            placeholder={
              dossier.pieces.some((p) => p.aClasser)
                ? 'Taper pour chercher un dossier…'
                : 'Vide : l’assistant classe (d’après l’indication)'
            }
            options={dossiersPlan.map((d) => ({ valeur: d.id, libelle: cheminLisible(d.chemin) }))}
          />
          {dossierId && (
            <button type="button" className="bouton-texte" onClick={() => setDossierId('')}>
              Laisser l’assistant classer
            </button>
          )}
        </div>
      )}

      {aRegler && (
        <fieldset className="champ">
          <legend>Facture reçue dans une boîte personnelle : qui l’a réglée ?</legend>
          {(
            [
              ['societe', 'La société'],
              ['avance', 'Avancée par la propriétaire de la boîte'],
            ] as const
          ).map(([v, libelle]) => (
            <label key={v} className="champ-case">
              <input
                type="radio"
                name={`${idDossier}-reglement`}
                checked={reglement === v}
                onChange={() => setReglement(v)}
              />{' '}
              {libelle} <span className="discret">{CONSEQUENCES_REGLEMENT[v]}</span>
            </label>
          ))}
        </fieldset>
      )}

      <div className="pile">
        {dossier.pieces.map((p) => {
          const c = correction(p)
          return (
            <div key={p.id} className="ligne">
              <div className="ligne-contenu">
                <div>
                  <strong>{p.nom}</strong>{' '}
                  <span className="badge">{LIBELLES_TYPE_PIECE[p.type]}</span>
                  {p.aRattacher && <span className="badge">À rattacher</span>}
                  {p.aRegler && <span className="badge">Règlement à dire</span>}
                  {p.aClasser && <span className="badge">À classer</span>}
                  {!p.aRattacher && p.statut === 'rattachee' && p.entite && (
                    <span className="badge">{p.entite}</span>
                  )}
                  {p.statut === 'ignoree' && <span className="badge">Écartée</span>}
                  {p.statut === 'doublon' && <span className="badge">Doublon</span>}
                </div>
                <div className="discret">
                  {[p.emetteur, p.numero && `n° ${p.numero}`, p.montant]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                {p.aRegler && <DebitsDeLaPiece pieceId={p.id} />}
                {p.aClasser && p.rangementErreur && (
                  <div className="discret">{p.rangementErreur}</div>
                )}
                {p.aRattacher && (
                  <>
                    {!c.ecarter && p.suites.length > 0 && (
                      <label className="champ-case">
                        <input
                          type="checkbox"
                          checked={c.suites}
                          onChange={(e) => corriger(p, { suites: e.target.checked })}
                        />{' '}
                        Créer : {p.suites.join(' ; ')}
                      </label>
                    )}
                    <div className="boutons">
                      <label className="champ-case">
                        <input
                          type="checkbox"
                          checked={c.ecarter}
                          onChange={(e) => corriger(p, { ecarter: e.target.checked })}
                        />{' '}
                        Écarter (publicité, reçu en double, sans rapport)
                      </label>
                      {!c.ecarter && (
                        <select
                          aria-label={`Entité de ${p.nom}`}
                          value={c.entiteId}
                          onChange={(e) => corriger(p, { entiteId: e.target.value })}
                        >
                          <option value="">Comme l’ensemble</option>
                          {pourPiece(p).map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.nom}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </>
                )}
              </div>
              <Link
                to="/pieces/$id"
                params={{ id: p.id }}
                className="bouton-discret"
                title="La pièce dans l’appli : lecture, montants, paiement, classement"
              >
                Fiche de la pièce
              </Link>
            </div>
          )
        })}
      </div>

      <div className="boutons">
        <button
          type="button"
          className="bouton bouton-principal"
          disabled={occupe}
          onClick={() => void valider()}
        >
          {occupe ? 'Validation…' : 'Valider le mail'}
        </button>
      </div>
      {erreur && <p className="alerte">{erreur}</p>}
    </div>
  )
}
